import { useState } from "react";
import { Copy, Check, Clock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { copyText } from "@/pages/landing/Checkout";
import { CopyRow, StepTitle, UploadBox, WhatsAppBox } from "@/pages/free/Unlock";

export const useTx = () => {
  const { user } = useAuth();
  return (en, fr) => (user?.language === "fr" ? fr : en);
};

export const isWaiting = (r) => r?.payment_status === "pending" && (r.proof_submitted || r.whatsapp_sent);

export function WaitingCard({ testid = "payment-waiting" }) {
  const tx = useTx();
  return (
    <div className="rounded-3xl bg-amber-50 border border-amber-200 p-5" data-testid={testid}>
      <div className="flex items-center gap-3"><Clock className="h-7 w-7 text-amber-600 shrink-0" /><div className="font-display font-extrabold text-lg text-[#1E2B3C]">{tx("Waiting for Payment Approval", "En attente de validation du paiement")}</div></div>
      <p className="text-[15px] text-[#5C4A1A] mt-2">{tx("Thank you! We are checking your payment. You don’t need to pay again.", "Merci ! Nous vérifions votre paiement. Inutile de payer à nouveau.")}</p>
    </div>
  );
}

export function PaymentPanel({ data, product, idea, onDone }) {
  const { user } = useAuth();
  const tx = useTx();
  const p = data.payment;
  const [copied, setCopied] = useState(false);
  const label = product === "builder" ? "Business Builder (1 year)" : "Promote My Business — Expert Coaching";
  const all = `${label}\nAmount: Rs ${p.amount}\nCompany: ${p.company_name}\nJuice: ${p.juice_number}\nBank: ${p.bank_name}\nAccount: ${p.bank_account}`;
  const copyAll = async () => { if (await copyText(all)) { setCopied(true); toast.success(tx("Payment details copied", "Détails copiés")); setTimeout(() => setCopied(false), 1800); } };
  const name = `${user.first_name || ""} ${user.last_name || ""}`.trim();
  const biz = data.business_name || idea?.name;
  const msg = `Hello Sidebizz, I have paid Rs ${p.amount} for ${label}${biz ? ` (${biz})` : ""}.\nMy name: ${name}\nMy account email: ${user.email}\nHere is my payment proof.`;
  const q = idea?.number ? `?idea=${idea.number}` : "";
  return (
    <div className="space-y-5" data-testid={`payment-panel-${product}`}>
      <section className="sb-card p-5 space-y-3">
        <StepTitle n={1}>{tx("Make your payment", "Faites votre paiement")}</StepTitle>
        <div className="rounded-2xl bg-[#1E2B3C] text-white p-4 flex items-center justify-between"><span className="text-white/70">{tx("Amount", "Montant")}</span><span className="font-display font-extrabold text-2xl" data-testid="payment-amount">Rs {Number(p.amount).toLocaleString("en-US")}</span></div>
        <div>
          <CopyRow k="company" label={tx("Company name", "Société")} value={p.company_name} />
          <CopyRow k="juice" label={tx("Juice number", "Numéro Juice")} value={p.juice_number} btn={tx("Copy Juice Number", "Copier le numéro Juice")} />
          <CopyRow k="bank" label={tx("Bank", "Banque")} value={p.bank_name} />
          <CopyRow k="account" label={tx("Bank account number", "Numéro de compte")} value={p.bank_account} btn={tx("Copy Bank Account Number", "Copier le numéro de compte")} />
        </div>
        <button onClick={copyAll} className="w-full h-11 rounded-xl border border-[#1E2B3C] font-bold text-sm inline-flex items-center justify-center gap-2" data-testid="payment-copy-all">{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{tx("Copy all payment details", "Copier tous les détails")}</button>
      </section>
      <section className="sb-card p-5 space-y-4">
        <StepTitle n={2}>{tx("Send your payment proof", "Envoyez votre preuve de paiement")}</StepTitle>
        <UploadBox endpoint={`/upgrade/${product}/proof`} extra={{ idea: idea?.number }} onDone={() => onDone("upload")} />
        <div className="border-t border-[#EADEC9] pt-4">
          <WhatsAppBox data={data} message={msg} endpoint={`/upgrade/${product}/whatsapp${q}`} onSent={() => onDone("wa")} label={tx("Send Payment Proof via WhatsApp", "Envoyer la preuve via WhatsApp")}
            hint={tx("In WhatsApp, add your payment screenshot, then press send.", "Dans WhatsApp, ajoutez la capture de votre paiement puis envoyez.")} />
        </div>
      </section>
    </div>
  );
}
